import { createMigrate } from "redux-persist";

// Root redux-persist migrations, version-gated via createMigrate.
//
// Why: the previous raw `migrate` ran every historical block on EVERY rehydration
// (it never checked the persisted `_persist.version`). The v4 block cleared
// `columns.vacancy_analysis.allColumns` on every page load, so Property View column
// order did not survive a hard refresh. With createMigrate, a given migration runs
// ONLY when upgrading past its version — a user already on the current version (6)
// runs none of them, so valid persisted column state is preserved.
//
// Each migration below reproduces the original block's transformation exactly; only
// the execution gating changed.

const addTableIfMissing = (state, key) => {
  if (state?.columns && !state.columns[key]) {
    state.columns[key] = {
      visibleColumns: [],
      customColumns: [],
      allColumns: [],
      initialized: false,
    };
  }
  return state;
};

const forceReinit = (state, key) => {
  if (state?.columns?.[key]) {
    state.columns[key].initialized = false;
    state.columns[key].allColumns = [];
  }
  return state;
};

export const migrations = {
  // v2: Add LeaseUp table type if missing.
  2: (state) => addTableIfMissing(state, "LeaseUp"),
  // v3: Add vacancy_analysis table type if missing.
  3: (state) => addTableIfMissing(state, "vacancy_analysis"),
  // v4: One-time re-init of vacancy_analysis to pick up mandatory columns.
  4: (state) => forceReinit(state, "vacancy_analysis"),
  // v5: One-time re-init of LeaseUp to pick up mandatory columns.
  5: (state) => forceReinit(state, "LeaseUp"),
  // v6: One-time reset of manager_review so custom columns start hidden by default.
  6: (state) => {
    if (state?.columns?.manager_review) {
      state.columns.manager_review.allColumns = [];
      state.columns.manager_review.visibleColumns = [];
      state.columns.manager_review.customColumns = [];
      state.columns.manager_review.initialized = false;
    }
    return state;
  },
  // v7: Add the Investor Report Comparison/Aggregated PnL sections to existing
  // users' persisted `appliedSections` so the new tables show on by default.
  7: (state) => {
    const applied = state?.ownerReportFilter?.appliedSections;
    if (Array.isArray(applied)) {
      ["comparative_pnl", "aggregated_pnl"].forEach((key) => {
        if (!applied.includes(key)) applied.push(key);
      });
    }
    return state;
  },
  // v8: Select Work Order Labor Summary by default for existing users too.
  8: (state) => {
    const applied = state?.ownerReportFilter?.appliedSections;
    if (Array.isArray(applied) && !applied.includes("work_order_summary")) {
      applied.push("work_order_summary");
    }
    return state;
  },
};

// Current persist version is 8: users already at 8 run no migrations and keep their
// column configuration; users upgrading from an older version run only the steps after
// their persisted version, exactly once.
export const rootMigrate = createMigrate(migrations, { debug: false });
